import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

/**
 * Return type for the useAccessControl hook
 */
interface UseAccessControlReturn {
  /** Check if user can access a feature based on required plan */
  canAccess: (requiredPlan: string) => boolean;
  /** Check access and show toast if denied */
  checkAccess: (requiredPlan: string, featureName?: string) => boolean;
  /** Whether the current user is an admin */
  isAdmin: boolean;
  /** Current user's plan level */
  userPlan: string;
}

/**
 * Plan hierarchy for access control
 * Higher number = higher tier with more access
 */
const planHierarchy = { free: 0, basic: 1, pro: 2, premium: 3 };

/**
 * Hook for managing user access control based on subscription plans
 * 
 * @description
 * Provides role-based access control (RBAC) functionality by checking user's
 * subscription plan against required plan tiers. Admins have full access to all features.
 * 
 * @example
 * ```tsx
 * function PremiumFeature() {
 *   const { checkAccess, userPlan } = useAccessControl();
 *   
 *   const handlePremiumAction = () => {
 *     if (!checkAccess('premium', 'Advanced Analytics')) {
 *       return; // Toast shown automatically
 *     }
 *     // Proceed with premium action
 *   };
 *   
 *   return <div>Current plan: {userPlan}</div>;
 * }
 * ```
 * 
 * @returns {UseAccessControlReturn} Access control utilities
 */
export function useAccessControl(): UseAccessControlReturn {
  const { profile } = useAuth();
  const { toast } = useToast();
  
  const userPlan = profile?.plano || 'free';
  const isAdmin = profile?.is_admin || false;
  
  /**
   * Check if user can access a feature based on plan hierarchy
   * Admins bypass all restrictions
   * 
   * @param requiredPlan - Minimum plan level required (free/basic/pro/premium)
   * @returns true if user has access, false otherwise
   */
  const canAccess = (requiredPlan: string): boolean => {
    // Administradores têm acesso completo
    if (isAdmin) return true;
    
    // Verificar hierarquia de planos
    return planHierarchy[userPlan as keyof typeof planHierarchy] >= 
           planHierarchy[requiredPlan as keyof typeof planHierarchy];
  };
  
  /**
   * Check access and automatically show error toast if denied
   * 
   * @param requiredPlan - Minimum plan level required
   * @param featureName - Optional feature name to show in error message
   * @returns true if user has access, false otherwise
   */
  const checkAccess = (requiredPlan: string, featureName?: string): boolean => {
    const hasAccess = canAccess(requiredPlan);
    
    if (!hasAccess) {
      toast({
        title: "Acesso Restrito",
        description: `Você precisa do plano ${requiredPlan} ou superior${featureName ? ` para acessar: ${featureName}` : ''}.`,
        variant: "destructive"
      });
    }
    
    return hasAccess;
  };
  
  return {
    canAccess,
    checkAccess,
    isAdmin,
    userPlan
  };
}